import moment from 'moment';
import i18next from 'i18next';
import { intervalToDuration, isDate, isValid, parseISO, format, parse, set } from 'date-fns';

export const getExtensiveAge = (birthdate: string, currentDate?: string) => {
  if (!birthdate) return '';

  const date = currentDate ? moment(currentDate, 'YYYY-MM-DD') : moment();
  const birthday = moment(birthdate, 'YYYY-MM-DD');
  // intervalToDuration throws on an impossible date (0000-00-00)
  if (!date.isValid() || !birthday.isValid()) return '';

  const duration = intervalToDuration({
    start: birthday.toDate(),
    end: date.toDate(),
  });

  const patientYears = duration.years;
  const patientMonths = duration.months;
  const patientDays = duration.days;

  const _ = i18next.getFixedT(i18next.dir(), 'global');
  const patientDateParts = [];

  if (patientYears > 0) {
    const suffix = patientYears > 1 ? _('label/years') : _('label/year');

    patientDateParts.push(`${patientYears} ${suffix}`);
  }

  if (patientMonths > 0 && patientYears <= 15) {
    const suffix = patientMonths > 1 ? _('label/months') : _('label/month');

    patientDateParts.push(`${patientMonths} ${suffix}`);
  }

  if (patientDays > 0 && patientYears < 1) {
    const suffix = patientDays > 1 ? _('label/days') : _('label/low_day');

    patientDateParts.push(`${patientDays} ${suffix}`);
  }

  const patientDate = patientDateParts.length > 1 ? patientDateParts.join(' ' + _('label/date_and') + ' ') : patientDateParts[0];

  return patientDate;
};

// Noon rather than the current time: after 21:00 in Brazil, the current time reaches the API as tomorrow (UTC)
const today = () => set(new Date(), { hours: 12, minutes: 0, seconds: 0, milliseconds: 0 });

const parseDate = (date: string | Date | any) => {
  if (isDate(date)) return date;

  if (moment.isMoment(date)) return date.toDate();

  if (typeof date !== 'string') return null;

  const isISODate = date.match(/^\d{4}-\d{2}-\d{2}$/);

  if (isISODate) return parseISO(date);

  const isFormattedDate = date.match(/^\d{2}\/\d{2}\/\d{4}$/);

  if (isFormattedDate) return parse(date, 'dd/MM/yyyy', new Date());

  return null;
};

// Anything unreadable or impossible (0000-00-00, 31/02/2025, an invalid Date or moment) is today:
// date-fns' format throws on an invalid date, and that takes the whole screen down
export const toDate = (date: string | Date | any) => {
  const parsed = parseDate(date);

  return parsed && isValid(parsed) ? parsed : today();
};

export const formatDate = (date: string | Date | any) => {
  const parsedDate = toDate(date);

  return format(parsedDate, 'dd/MM/yyyy');
};

export const formatDateToApi = (date: string | Date | any) => {
  const parsedDate = toDate(date);

  return format(parsedDate, 'yyyy-MM-dd HH:mm:ss');
};
